import { useEffect, useRef, useState } from 'react';
import PublicReviewShell from '../../components/PublicReviewShell.jsx';
import PageState from '../../components/PageState.jsx';
import Notice from '../../components/Notice.jsx';
import { api } from '../../lib/api.js';
import { longDate, personName, plural, takeReviewFragment } from '../../lib/access-review-format.js';
import ReviewWorkspace from './ReviewWorkspace.jsx';

// Plain wording: nothing says which assignments or which customer when the link does not work, and nothing offers a login.
const MESSAGES = Object.freeze({
  invalid: 'This link is not valid. Open the newest email about your review again.',
  revoked: 'This link no longer works: your assignments were moved to another reviewer, or a newer email replaced this one. Use the link in the newest email.',
  busy: 'Too many attempts. Try again later.',
  offline: 'The onGuard portal could not be reached. Try again later.',
});

/** The refusal of a link (contract: 410 expired, closed or revoked; 404; 429) as a page phase. */
export function refusalOf(status, body) {
  if (status === 410 && body?.state === 'expired') return { phase: 'expired', expiredAt: body.expiredAt ?? null, owner: body.owner ?? null };
  if (status === 410 && body?.state === 'closed') return { phase: 'closed', closedAt: body.closedAt ?? null, decisions: body.decisions ?? null };
  if (status === 410) return { phase: 'revoked' };
  if (status === 429) return { phase: 'busy' };
  return { phase: 'invalid' };
}

/**
 * The reviewer decision view at /access-reviews/review (spec 13.2, 7.2), outside the portal shell like /watch/review. The
 * token is read once from the URL fragment (#t=..&i=<itemRef>&d=keep) and removed from the address bar before the first
 * call. It travels only in POST bodies. A page load only views: the decision from the link is preselected on its row,
 * never saved, until the reviewer presses Save.
 */
export default function ReviewPage() {
  const token = useRef(null);
  const started = useRef(false);
  const [phase, setPhase] = useState({ phase: 'loading' });
  const [view, setView] = useState(null);
  const [preselect, setPreselect] = useState(null);
  const [newLink, setNewLink] = useState({ busy: false, sent: false, error: null });

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const frag = takeReviewFragment(window);
    token.current = frag.t ?? null;
    if (!frag.t) { setPhase({ phase: 'invalid' }); return; }
    if (frag.i) setPreselect({ itemRef: frag.i, decision: frag.d ?? null });
    api.reviewView({ token: frag.t })
      .then((r) => {
        if (r.status === 200 && r.body?.state === 'ready' && Array.isArray(r.body.items) && r.body.formNonce) {
          setView(r.body);
          setPhase({ phase: 'ready' });
        } else {
          setPhase(refusalOf(r.status, r.body));
        }
      })
      .catch(() => setPhase({ phase: 'offline' }));
  }, []);

  const nonce = () => view?.formNonce;
  const adapter = {
    decide: ({ decisions, bulk, code }) => api.reviewDecide({ token: token.current, formNonce: nonce(), decisions, ...(bulk ? { bulk: true } : {}), ...(code ? { code } : {}) }),
    requestCode: () => api.reviewCode({ token: token.current, formNonce: nonce() }),
    delegate: ({ itemRefs, toId, reason }) => api.reviewDelegate({ token: token.current, formNonce: nonce(), itemRefs, toId, reason }),
    searchPeople: ({ q, item }) => api.reviewPeople({ token: token.current, formNonce: nonce(), q, ...(item ? { item } : {}) }),
  };

  const askNewLink = async () => {
    setNewLink({ busy: true, sent: false, error: null });
    try {
      const r = await api.reviewNewLink({ token: token.current });
      if (r.status === 200 && r.body?.state === 'sent') setNewLink({ busy: false, sent: true, error: null });
      else setNewLink({ busy: false, sent: false, error: r.status === 429 ? (r.body?.message ?? MESSAGES.busy) : (r.body?.message ?? 'A new link could not be sent. Ask the person who requested the review.') });
    } catch {
      setNewLink({ busy: false, sent: false, error: MESSAGES.offline });
    }
  };

  let content;
  const p = phase.phase;
  if (p === 'loading') content = <PageState state="loading" cards={0} label="Opening your review" />;
  else if (p === 'ready') {
    content = <ReviewWorkspace view={view} adapter={adapter} preselect={preselect} onGone={(body) => setPhase(refusalOf(410, body))} />;
  } else if (p === 'expired') {
    const owner = phase.owner;
    content = (
      <Notice
        tone="warn" titleAs="h1" testId="review-expired"
        title={`This link expired${phase.expiredAt ? ` on ${longDate(phase.expiredAt)}` : ''}.`}
        action={newLink.sent ? null : <button type="button" className="btn-primary min-h-[40px]" disabled={newLink.busy} onClick={askNewLink} data-testid="review-new-link">Send me a new link</button>}
      >
        {newLink.sent
          ? 'We sent a new link to your email address. Open the newest email to continue.'
          : `${owner ? `Ask ${personName(owner)}${owner.address ? ` (${owner.address})` : ''} for a new one, or send one to yourself` : 'Send yourself a new one'}; the review is still open.`}
        {newLink.error ? <span role="alert" className="mt-1 block text-ipc-ink">{newLink.error}</span> : null}
      </Notice>
    );
  } else if (p === 'closed') {
    content = (
      <Notice tone="info" titleAs="h1" testId="review-closed" title={`This review closed${phase.closedAt ? ` on ${longDate(phase.closedAt)}` : ''}.`}>
        {Number.isInteger(phase.decisions) ? `Your ${plural(phase.decisions, 'decision')} ${phase.decisions === 1 ? 'is' : 'are'} in the report.` : 'Your decisions are in the report.'} Nothing else is needed from you.
      </Notice>
    );
  } else {
    content = <Notice tone={p === 'busy' || p === 'offline' ? 'warn' : 'bad'} titleAs="h1" title={MESSAGES[p] ?? MESSAGES.invalid} testId={`review-${p}`} />;
  }
  return (
    <PublicReviewShell tenantName={view?.tenantName ?? ''} reviewerName={p === 'ready' ? view?.reviewer?.displayName ?? '' : ''}>
      {content}
    </PublicReviewShell>
  );
}
